import {
  generateID,
  getJsonKeyFromField,
  getObjectById,
  getV2ValueWP,
  SectionId,
  spErrorTags,
  structuredCloneL,
  tryParseJSON,
} from "@eventr/common";
import { IEditComponent } from "data/Data";
import type { PushPathOptions } from "feat/globalRouter/performPush";
import { routes } from "routing";
import { bs } from "state/bound.store";
import { isUserPartOfUsersWithTags } from "v2/v2Utils";
import { mergeDraftItems } from "../../utils/merge-draft-items";
import { findEditSubItemPageId } from "./find-edit-sub-item-page-id";
import { findListSubItemPageId } from "./find-list-sub-item-page-id";
import {
  PENDING_SUB_ITEM_STATE_KEY,
  STAGE_AND_NAVIGATE_ON_JOIN_SECTIONS,
  type PendingSubItemState,
} from "./pending-sub-item-navigation";

type PushPath = (path: string, options?: PushPathOptions) => Promise<void>;

// Activities and Transport edit their current item via react-hook-form against a fixed key,
// rather than the generic field-driven joinLeaveButton component every other section uses.
const HARDCODED_USER_LIST_KEY: Partial<Record<SectionId, string>> = {
  [SectionId.ACTIVITY]: "attendees_list",
  [SectionId.TRANSPORT]: "passengers_list",
};

interface IUserListFieldConfig {
  userListFieldId: number;
  childUserListId?: number;
}

// A section's "join" affordance is configured one of two ways: a dedicated
// joinLeaveButton component (Hotel, Flight, Hire Car - settings.userListFieldId /
// settings.childUserListId), or, for sections with no joinLeaveButton at all (Parking), the
// same list/child-list pair sits directly on its "attendees" (team management) component
// instead (fieldId / userListFieldId).
function findUserListFieldConfig(components: IEditComponent[] | undefined): IUserListFieldConfig | undefined {
  if (!components) return undefined;
  for (const component of components) {
    if (component.type === "joinLeaveButton" && component.settings?.userListFieldId) {
      return {
        userListFieldId: component.settings.userListFieldId,
        childUserListId: component.settings.childUserListId,
      };
    }
    if (component.type === "attendees" && component.fieldId) {
      return {
        userListFieldId: component.fieldId,
        childUserListId: component.userListFieldId,
      };
    }
    const found = findUserListFieldConfig(component.components);
    if (found) return found;
  }
  return undefined;
}

/**
 * Joins the current user to a section item's user list, without opening the item's edit
 * screen.
 *
 * Activities/Transport and simple (non-nested) sections persist immediately, mirroring
 * whatever the corresponding edit form's own Join button would do (skipping unrelated
 * field validation, same as that button does). For a nested child list, Hotel stages the
 * new room as a draft and navigates into its edit screen, while Parking navigates to the
 * item's cars list screen without creating anything.
 *
 * Takes the caller's own `pushPath` (from `useGlobalRouter()`) rather than `window.pushPath`,
 * which follows the frontmost view and can change mid-flight (a tab switch, a sheet opening)
 * while an async caller is still running.
 */
export async function quickJoinSectionItem(sectionId: SectionId, itemId: string, pushPath: PushPath): Promise<boolean> {
  const currentUser = bs().getCurrentUser();
  if (!currentUser) {
    spErrorTags("Current user not found", ["QUICK_JOIN"]);
    return false;
  }

  const sectionV2Data = bs().getSectionV2FromSectionIdFromSelectedItinerary(sectionId);
  if (!sectionV2Data) {
    spErrorTags("Section data not found", ["QUICK_JOIN"], { sectionId });
    return false;
  }

  // Merges in any existing draft for this section so a quick-join never clobbers an
  // unsaved edit sitting in another open view.
  const rawValues = structuredCloneL(sectionV2Data.data);
  const topLevelKey = rawValues && typeof rawValues === "object" ? Object.keys(rawValues)[0] : undefined;
  const values = mergeDraftItems(rawValues, topLevelKey, bs().draftSectionItems[bs().selectedItinID]?.[sectionId]);

  const item = getObjectById(itemId, values);
  if (!item) {
    spErrorTags("Item not found", ["QUICK_JOIN"], { sectionId, itemId });
    return false;
  }

  const hardcodedKey = HARDCODED_USER_LIST_KEY[sectionId];
  if (hardcodedKey) {
    const list = Array.isArray(item[hardcodedKey]) ? item[hardcodedKey] : [];
    const alreadyJoined = list.some((group: any) => group.users?.some((user: any) => user.option === currentUser.id));
    if (alreadyJoined) return true;

    item[hardcodedKey] = [
      ...list,
      { _id: generateID(bs().selectedItinID, sectionId), users: [{ option: currentUser.id, type: "User" }] },
    ];

    if (sectionId === SectionId.ACTIVITY) {
      const resp = await bs().upsertElement(sectionId, item);
      return resp.status === "success";
    }
    return (await bs().updateItinerarySection(sectionId, item)) === "success";
  }

  const section = bs().getSection(sectionId);
  const joinConfig = findUserListFieldConfig(section?.properties?.mobileEditFieldOptions?.components);

  const userListField = joinConfig ? bs().getFieldById(joinConfig.userListFieldId) : undefined;
  if (!userListField) {
    spErrorTags("No join field configured for section", ["QUICK_JOIN"], { sectionId });
    return false;
  }
  const jsonKey = getJsonKeyFromField(userListField);
  const childUserListId = joinConfig?.childUserListId;

  if (childUserListId) {
    if (sectionId === SectionId.PARKING) {
      const pageId = findListSubItemPageId(
        section?.properties?.mobileEditFieldOptions?.subPages,
        joinConfig.userListFieldId,
      );
      if (pageId === undefined) {
        spErrorTags("No list page configured for join field", ["QUICK_JOIN"], {
          sectionId,
          userListFieldId: joinConfig.userListFieldId,
        });
        return false;
      }
      // Same reason as the booking push below: pushed on its own the list has no ancestor
      // screen, so there is no item Done to persist what the user picks.
      const pendingList: PendingSubItemState = { pageId };
      await pushPath(routes.edit.sectionItem.build({ sectionId, itemId }), {
        state: { [PENDING_SUB_ITEM_STATE_KEY]: pendingList },
      });
      return true;
    }
    const childField = bs().getFieldById(childUserListId);
    if (!childField) {
      spErrorTags("Child join field not found", ["QUICK_JOIN"], { sectionId, childUserListId });
      return false;
    }
    const childJsonKey = getJsonKeyFromField(childField);
    const userList = (item[jsonKey] ?? []) as any[];
    const alreadyJoined = userList.some((parent) => {
      const usersWithTag = tryParseJSON(getV2ValueWP(childJsonKey, parent), []);
      return isUserPartOfUsersWithTags(currentUser, usersWithTag);
    });
    if (alreadyJoined) return true;

    const newChildId = generateID(bs().selectedItinID, sectionId, joinConfig.userListFieldId);
    item[jsonKey] = [
      ...userList,
      {
        [childJsonKey]: [{ option: currentUser.id, type: "User" }],
        _id: newChildId,
      },
    ];

    if (STAGE_AND_NAVIGATE_ON_JOIN_SECTIONS.has(sectionId)) {
      // The staged item also rides in the push's router state for the section provider
      // to seed from.
      const stagedDraftItem = structuredCloneL(item);
      bs().addDraftSectionItem(sectionId, stagedDraftItem);
      const pageId = findEditSubItemPageId(
        section?.properties?.mobileEditFieldOptions?.subPages,
        joinConfig.userListFieldId,
      );

      if (pageId === undefined) {
        // The join itself is already staged at this point, but with no editSubItem page to
        // route into, the user has no way to see or complete it - surface this as a
        // failure (matching every other early-return above) rather than reporting success
        // with no visible effect.
        spErrorTags("No editSubItem page configured for join field", ["QUICK_JOIN"], {
          sectionId,
          userListFieldId: joinConfig.userListFieldId,
        });
        return false;
      }
      // Pushed straight from outside the /edit sheet, the booking's own edit screen would
      // present as a fresh single-view stack with no ancestor - so Cancel/Done can't land
      // back on the section item's own screen. Push that screen first and hand it the
      // pending booking; it pushes the booking itself once mounted (see
      // EditSectionsItemsPage), building the same two-level stack an ordinary two-tap
      // navigation would.
      const pending: PendingSubItemState = { pageId, subItemId: newChildId, draftItem: stagedDraftItem };
      const path = routes.edit.sectionItem.build({ sectionId, itemId });
      await pushPath(path, { state: { [PENDING_SUB_ITEM_STATE_KEY]: pending } });
      return true;
    }
  } else {
    const userList = (item[jsonKey] ?? []) as any[];
    if (isUserPartOfUsersWithTags(currentUser, userList)) return true;
    item[jsonKey] = [...userList, { option: currentUser.id, type: "User" }];
  }

  const result = await bs().updateItinerarySection(sectionId, item);
  return result === "success";
}
